import UserCard from "./UserCard";
import { useUsersInfiniteQuery } from "./useUsers";
import { Fragment, useState, useMemo } from "react";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import { Button } from "@shared/components/ui/button";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { Input } from "@shared/components/ui/input";
import { UserPlus, Search, Filter, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { startOfDay, endOfDay } from "date-fns";
import { SelectCity } from "@features/settings/citys/SelectCity";
import { Badge } from "@shared/components/ui/badge";
import { Card } from "@shared/components/ui/card";
import { useTranslation } from "react-i18next";

const UsersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(true);

  // Parse search params into filters object
  const params = useMemo(() => {
    const filters = { limit: 10 };
    
    // Role filter
    if (searchParams.get('role')) {
      filters.role = searchParams.get('role');
    }
    
    // Active status filter
    if (searchParams.get('isActive') !== null) {
      filters.isActive = searchParams.get('isActive') === 'true';
    }
    
    // Status filter
    if (searchParams.get('status')) {
      filters.status = searchParams.get('status');
    }
    
    // Brand name search
    if (searchParams.get('brandName_q')) {
      filters.brandName_q = searchParams.get('brandName_q');
    }
    
    // Full name search
    if (searchParams.get('fullName_q')) {
      filters.fullName_q = searchParams.get('fullName_q');
    }
    
    // City filter
    if (searchParams.get('city')) {
      filters.city = searchParams.get('city');
    }
    
    // Date range filters
    if (searchParams.get('createdAt_gte')) {
      filters.createdAt_gte = searchParams.get('createdAt_gte');
    }
    if (searchParams.get('createdAt_lte')) {
      filters.createdAt_lte = searchParams.get('createdAt_lte');
    }
    
    return filters;
  }, [searchParams]);

  // Helper function to update search params
  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams);
  };

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useUsersInfiniteQuery(params);

  const handleDateRangeChange = ({ from, to }) => {
    updateSearchParams({
      createdAt_gte: from ? startOfDay(from).toISOString() : undefined,
      createdAt_lte: to ? endOfDay(to).toISOString() : undefined,
    });
  };

  const handleCityChange = (selectedCity) => {
    updateSearchParams({
      city: selectedCity && selectedCity.name ? selectedCity.name : undefined,
    });
  };

  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const activeFiltersCount = Object.keys(params).filter(
    (key) => key !== "limit" && key !== "page"
  ).length;

  const totalUsers = data?.pages?.[0]?.totalItems || 0;

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold tracking-tight">
              {t("navigation.users")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("users.manageUsers")} {totalUsers > 0 && `(${totalUsers})`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                {t("main.clearFilters")}
                <Badge variant="secondary" className="ml-1">
                  {activeFiltersCount}
                </Badge>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              {t("main.filters")}
            </Button>
            <Button onClick={() => navigate("/users/create")} className="gap-2">
              <UserPlus className="h-4 w-4" />
              {t("users.createUser")}
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="mt-4 space-y-4 pt-4 border-t">
            {/* Role Filters */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {t("user.role.label")}
              </label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={
                    params.role === undefined &&
                    params.isActive === undefined &&
                    params.status === undefined
                      ? "default"
                      : "outline"
                  }
                  onClick={() => {
                    updateSearchParams({
                      role: undefined,
                      isActive: undefined,
                      status: undefined,
                    });
                  }}
                >
                  {t("main.all")}
                </Button>

                <Button
                  size="sm"
                  variant={params.role === "admin" ? "default" : "outline"}
                  onClick={() => updateSearchParams({ role: "admin" })}
                >
                  {t("role.admin")}
                </Button>
                <Button
                  size="sm"
                  variant={params.role === "manager" ? "default" : "outline"}
                  onClick={() => updateSearchParams({ role: "manager" })}
                >
                  {t("role.manager")}
                </Button>
                <Button
                  size="sm"
                  variant={params.role === "client" ? "default" : "outline"}
                  onClick={() => updateSearchParams({ role: "client" })}
                >
                  {t("role.client")}
                </Button>
                <Button
                  size="sm"
                  variant={params.role === "livreur" ? "default" : "outline"}
                  onClick={() => updateSearchParams({ role: "livreur" })}
                >
                  {t("role.livreur")}
                </Button>
                <Button
                  size="sm"
                  variant={params.role === "collector" ? "default" : "outline"}
                  onClick={() => updateSearchParams({ role: "collector" })}
                >
                  {t("user.role.collector")}
                </Button>
                <Button
                  size="sm"
                  variant={
                    params.role === "clientSupport" ? "default" : "outline"
                  }
                  onClick={() => updateSearchParams({ role: "clientSupport" })}
                >
                  {t("user.role.clientSupport")}
                </Button>
                <Button
                  size="sm"
                  variant={params.role === "accountant" ? "default" : "outline"}
                  onClick={() => updateSearchParams({ role: "accountant" })}
                >
                  {t("user.role.accountant")}
                </Button>
                <Button
                  size="sm"
                  variant={
                    params.isActive !== undefined ? "default" : "outline"
                  }
                  onClick={() => updateSearchParams({ isActive: "false" })}
                >
                  {t("status.inactive")}
                </Button>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("main.search")}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t("users.searchByNameOrBrand")}
                    className="pl-9"
                    value={params.brandName_q || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateSearchParams({ 
                        brandName_q: value
                      });
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("user.city")}
                </label>
                <SelectCity
                  city={params.city ? { name: params.city } : null}
                  onSelectCity={handleCityChange}
                  showAllOption={true}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("user.status.label")}
                </label>
                <Select
                  value={params.status || "all"}
                  onValueChange={(value) => {
                    updateSearchParams({
                      status: value === "all" ? undefined : value,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("user.status.label")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("main.all")}</SelectItem>
                    <SelectItem value="pending">
                      {t("user.status.pending")}
                    </SelectItem>
                    <SelectItem value="approved">
                      {t("user.status.approved")}
                    </SelectItem>
                    <SelectItem value="new_approved">
                      {t("user.status.new_approved")}
                    </SelectItem>
                    <SelectItem value="rejected">
                      {t("user.status.rejected")}
                    </SelectItem>
                    <SelectItem value="cancelled">
                      {t("user.status.cancelled")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("common.dateRange")}
                </label>
                <DatePickerWithRange
                  onSelectDate={handleDateRangeChange}
                  initialDate={{
                    from: params.createdAt_gte
                      ? new Date(params.createdAt_gte)
                      : null,
                    to: params.createdAt_lte
                      ? new Date(params.createdAt_lte)
                      : null,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Users List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <ThreeDotLoader />
        </div>
      ) : (
        <>
          {data?.pages?.[0]?.data?.length === 0 ? (
            <Card className="p-12">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Search className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg">{t("main.noResults")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("users.noUsersFound")}
                </p>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="mt-4"
                  >
                    {t("main.clearFilters")}
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {data?.pages?.map((page) => (
                <Fragment key={page.pageInfo.currentPage}>
                  {page.data.map((user) => (
                    <UserCard key={user._id} user={user} />
                  ))}
                </Fragment>
              ))}
            </div>
          )}
          <InfiniteScrollTrigger
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        </>
      )}
    </div>
  );
};

export default UsersList;
